import React, { useState } from 'react';
import { Copy, ChevronDown, ChevronUp, User, Briefcase, Calendar } from 'lucide-react';
import { copyToClipboard } from '../../utils/clipboard';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useChecklist } from '../../hooks/useChecklist';
import { useProgress } from '../../hooks/useProgress';
import { roleConfigs } from '../../data/roleConfigs';

const DetailedOnboardingChecklist = ({ initialProfile, onBackToRoles }) => {
  const [profile, setProfile] = useLocalStorage('onboardingProfile', 
    initialProfile || {
      role: '',
      experience: 'junior',
      os: 'macos',
      name: ''
    }
  );

  const [currentStep, setCurrentStep] = useState(1);
  const { checkedItems, handleCheck: toggleItem } = useChecklist('onboarding-checklist');
  const { getProgress } = useProgress();
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newState = { ...prev };
      if (newState[itemId]) {
        delete newState[itemId]; // Remove to prevent memory leak
      } else {
        newState[itemId] = true;
      }
      return newState;
    });
  };
  // Define the 9 progressive onboarding steps
  const onboardingSteps = [
    { id: 1, title: 'Resources', description: 'Role-specific learning materials adapted by experience level and OS' },
    { id: 2, title: 'Documentation', description: 'Team processes and guides for each role/level' },
    { id: 3, title: 'Team Directory', description: 'Role-specific contacts, mentors, and collaborators' },
    { id: 4, title: 'Work Environment', description: 'Setup instructions by role, experience, and OS' },
    { id: 5, title: 'Essential Tools', description: 'Primary/secondary/advanced tools by role and experience' },
    { id: 6, title: 'Collaboration Setup', description: 'Team workflows and processes by role/level' },
    { id: 7, title: 'Platform Access', description: 'Required platforms and access levels by role/experience' },
    { id: 8, title: 'Security Setup', description: 'Security requirements and procedures by role/level' },
    { id: 9, title: 'Team Workflow', description: 'Processes, responsibilities, and ceremonies by role' }
  ];

  const getDetailLevel = (experience) => {
    switch (experience) {
      case 'junior': return 'comprehensive';
      case 'mid': return 'concise';
      case 'senior': return 'minimal';
      default: return 'comprehensive';
    }
  };

  // Create comprehensive step data for each role and step
  const generateStepData = (stepNumber, role, stepInfo) => {
    const roleTitle = roleConfigs[role]?.title || role;
    
    return {
      id: `${role}-step-${stepNumber}`,
      text: `Complete ${stepInfo.title} for ${roleTitle}`,
      category: stepInfo.title,
      step: stepNumber,
      baseSteps: [{
        title: `${stepInfo.title} Configuration`,
        commands: [
          {
            description: `Set up ${stepInfo.title.toLowerCase()} for ${roleTitle}`,
            command: profile.os === 'macos' ? `# Configure ${stepInfo.title.toLowerCase()}` : 
                    profile.os === 'windows' ? `# Configure ${stepInfo.title.toLowerCase()}` : 
                    `# Configure ${stepInfo.title.toLowerCase()}`,
            action: `Follow ${roleTitle} guidelines for ${stepInfo.title.toLowerCase()} setup`,
            details: {
              comprehensive: `Complete the ${stepInfo.title.toLowerCase()} configuration following your team's established processes and guidelines. This includes understanding the purpose, setting up access, and verifying functionality.`,
              concise: `Set up ${stepInfo.title.toLowerCase()} per team guidelines and verify access`,
              minimal: `Configure ${stepInfo.title.toLowerCase()}`
            }
          },
          {
            description: `Verify ${stepInfo.title.toLowerCase()} functionality`,
            command: profile.os === 'macos' ? `# Test ${stepInfo.title.toLowerCase()} setup` : 
                    profile.os === 'windows' ? `# Test ${stepInfo.title.toLowerCase()} setup` : 
                    `# Test ${stepInfo.title.toLowerCase()} setup`,
            action: `Test and confirm all ${stepInfo.title.toLowerCase()} components are working correctly`,
            details: {
              comprehensive: `Thoroughly test all aspects of your ${stepInfo.title.toLowerCase()} setup to ensure everything functions correctly. Document any issues and seek help if needed.`,
              concise: `Test ${stepInfo.title.toLowerCase()} setup and confirm functionality`,
              minimal: `Verify ${stepInfo.title.toLowerCase()} works`
            }
          }
        ]
      }]
    };
  };

  // Generate universal steps for all roles dynamically
  const universalSteps = {};
  Object.keys(roleConfigs).forEach(roleKey => {
    universalSteps[roleKey] = {
      macos: {},
      windows: {},
      linux: {}
    };
    
    ['macos', 'windows', 'linux'].forEach(os => {
      for (let i = 1; i <= 9; i++) {
        const stepInfo = onboardingSteps[i - 1];
        universalSteps[roleKey][os][i] = generateStepData(i, roleKey, stepInfo);
      }
    });
  });

  const getStepsWithDetails = (stepData, experience) => {
    const detailLevel = getDetailLevel(experience);
    
    if (!stepData || !stepData.baseSteps) return [];
    
    return [{
      ...stepData,
      details: {
        overview: stepData.details?.overview || '',
        steps: stepData.baseSteps.map(stepGroup => ({
          ...stepGroup,
          commands: stepGroup.commands.map(cmd => ({
            ...cmd,
            note: cmd.details?.[detailLevel] || cmd.note || ''
          }))
        }))
      }
    }];
  };

  const currentRole = roleConfigs[profile.role];
  const roleSteps = universalSteps[profile.role]?.[profile.os] || {};
  
  // Get current step data
  const currentStepData = roleSteps[currentStep];
  const currentChecklist = currentStepData ? getStepsWithDetails(currentStepData, profile.experience) : [];
  
  // Calculate progress for current step
  const stepProgress = getProgress(currentChecklist, checkedItems);
  
  // Manual step navigation
  const goToStep = (stepNumber) => {
    if (stepNumber >= 1 && stepNumber <= 9) {
      setCurrentStep(stepNumber);
    }
  };

  const CommandBlock = ({ command, description, note, action }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 mb-2">{description}</p>
          {command && (
            <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm mb-2 relative">
              <span className="text-gray-500">$ </span>{command}
              <button
                onClick={() => copyToClipboard(command)}
                className="absolute top-2 right-2 p-1 hover:bg-gray-800 rounded"
                title="Copy command"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}
          {action && (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm mb-2">
              <strong>Action:</strong> {action}
            </div>
          )}
          {note && (
            <div className="text-sm text-gray-600 mt-2">
              <strong>Note:</strong> {note}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!profile.role) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Your Role</h2>
          <p className="text-gray-600 mb-8">Choose your role to see personalized onboarding steps</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {Object.entries(roleConfigs).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setProfile(prev => ({ ...prev, role: key }))}
                className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
              >
                <h3 className="font-semibold text-gray-900">{config.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{config.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const currentStepInfo = onboardingSteps[currentStep - 1];
  
  // Get role-based color classes (static for Tailwind)
  const getColorClasses = () => {
    const roleColor = currentRole?.color || 'blue';
    
    const colorMap = {
      blue: {
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700',
        progress: 'bg-blue-600',
        checkbox: 'bg-blue-600 border-blue-600'
      },
      green: {
        bg: 'bg-green-100',
        text: 'text-green-600',
        button: 'bg-green-600 hover:bg-green-700',
        progress: 'bg-green-600',
        checkbox: 'bg-green-600 border-green-600'
      },
      purple: {
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700',
        progress: 'bg-purple-600',
        checkbox: 'bg-purple-600 border-purple-600'
      },
      orange: {
        bg: 'bg-orange-100',
        text: 'text-orange-600',
        button: 'bg-orange-600 hover:bg-orange-700',
        progress: 'bg-orange-600',
        checkbox: 'bg-orange-600 border-orange-600'
      },
      red: {
        bg: 'bg-red-100',
        text: 'text-red-600',
        button: 'bg-red-600 hover:bg-red-700',
        progress: 'bg-red-600',
        checkbox: 'bg-red-600 border-red-600'
      }
    };
    
    return colorMap[roleColor] || colorMap.blue;
  };
  
  const colors = getColorClasses();

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${colors.bg} ${colors.text}`}>
              <Briefcase className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{currentRole?.title} Onboarding</h1>
              <p className="text-gray-600">Progressive step-by-step guidance</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => onBackToRoles ? onBackToRoles() : setProfile(prev => ({ ...prev, role: '' }))}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
            >
              Change Role
            </button>
          </div>
        </div>

        {/* Profile Selection Bar */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Experience Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
              <select
                value={profile.experience}
                onChange={(e) => setProfile(prev => ({ ...prev, experience: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="junior">Junior - Comprehensive guidance</option>
                <option value="mid">Mid-level - Concise instructions</option>
                <option value="senior">Senior - Minimal details</option>
              </select>
            </div>

            {/* Operating System */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Operating System</label>
              <select
                value={profile.os}
                onChange={(e) => setProfile(prev => ({ ...prev, os: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="macos">macOS</option>
                <option value="windows">Windows</option>
                <option value="linux">Linux</option>
              </select>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Name (Optional)</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Step Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            {onboardingSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => goToStep(step.id)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep === step.id
                    ? `${colors.progress} text-white`
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {step.id}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => goToStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => goToStep(Math.min(9, currentStep + 1))}
              disabled={currentStep === 9}
              className={`px-3 py-1 text-sm rounded text-white ${colors.button} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Next
            </button>
          </div>
        </div>

        {/* Current Step Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Step {currentStep}: {currentStepInfo?.title}
              </h2>
              <p className="text-gray-600 mt-1">{currentStepInfo?.description}</p>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                Progress: {Math.round(stepProgress)}%
              </span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${colors.progress}`}
              style={{ width: `${stepProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="space-y-6">
        {currentChecklist.length > 0 ? (
          currentChecklist[0].details?.steps?.map((stepGroup, groupIndex) => (
            <div key={groupIndex} className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{stepGroup.title}</h3>
              <div className="space-y-4">
                {stepGroup.commands?.map((command, cmdIndex) => {
                  const itemId = `${currentStep}-${groupIndex}-${cmdIndex}`;
                  const isChecked = checkedItems[itemId] || false;
                  const isExpanded = expandedItems[itemId] || false;

                  return (
                    <div key={cmdIndex} className="border border-gray-200 rounded-lg">
                      <div className="p-4">
                        <div className="flex items-start space-x-3">
                          <button
                            onClick={() => toggleItem(itemId)}
                            className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isChecked
                                ? colors.checkbox
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            {isChecked && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className={`font-medium ${isChecked ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                {command.description}
                              </h4>
                              <button
                                onClick={() => toggleExpanded(itemId)}
                                className="p-1 hover:bg-gray-100 rounded"
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="mt-3">
                                <CommandBlock
                                  command={command.command}
                                  description={command.description}
                                  action={command.action}
                                  note={command.note}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <div className="text-gray-400 mb-4">
              <Briefcase className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No step data available</h3>
            <p className="text-gray-600">
              Step content for {currentRole?.title} on {profile.os} is being prepared.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedOnboardingChecklist;
